/**
 * Split Bill Modal - Feature G
 * Quick bill splitting for groups
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function SplitBillModal({ isOpen, onClose, cart, totalAmount }) {
  const [guestCount, setGuestCount] = useState(2);
  const [splitting, setSplitting] = useState(false);

  const handleSplitEvenly = async () => {
    if (guestCount < 2) {
      toast.error('Mindestens 2 Gäste erforderlich');
      return;
    }

    const amountPerGuest = totalAmount / guestCount;
    
    toast.success('Rechnung aufgeteilt!', {
      description: `${guestCount} x ${amountPerGuest.toFixed(2)}€`
    });
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-[#0E1628] border-slate-700/50">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
        
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Users className="w-6 h-6 text-blue-400" />
            Rechnung teilen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <div className="text-sm text-slate-400 mb-1">Gesamtbetrag</div>
            <div className="text-3xl font-bold text-white" style={{ fontFamily: "'Fira Code', monospace" }}>
              {totalAmount.toFixed(2)}€
            </div>
          </div>

          <div>
            <Label className="text-slate-300 mb-2 block">Anzahl Gäste</Label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setGuestCount(Math.max(2, guestCount - 1))}
                className="w-12 h-12 border-slate-600 hover:bg-slate-700"
              >
                -
              </Button>
              <Input
                type="number"
                min="2"
                value={guestCount}
                onChange={(e) => setGuestCount(Math.max(2, parseInt(e.target.value) || 2))}
                className="text-center text-2xl font-bold bg-[#111C32] border-slate-700 text-white h-12"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              />
              <Button
                variant="outline"
                onClick={() => setGuestCount(guestCount + 1)}
                className="w-12 h-12 border-slate-600 hover:bg-slate-700"
              >
                +
              </Button>
            </div>
          </div>

          <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <div className="text-sm text-slate-400 mb-1">Pro Gast</div>
            <div className="text-2xl font-bold text-blue-400" style={{ fontFamily: "'Fira Code', monospace" }}>
              {(totalAmount / guestCount).toFixed(2)}€
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-white hover:bg-slate-700">
            Abbrechen
          </Button>
          <Button
            onClick={handleSplitEvenly}
            disabled={splitting}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Gleichmäßig teilen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
